import { useState, useEffect, useCallback, useMemo } from 'react'; 
import styles from '../../Admin/Universidades/Formulario.module.css';
import Swal from 'sweetalert2'; 
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useAuth } from '../../Auth/AuthContext';

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/perfil`;

const USER_CONFIG = {
    1: { // Estudiante
        campos: ['nombre', 'appa', 'apma', 'correo', 'curp', 'fechaNacimiento', 'estado', 'municipio', 'bachillerato', 'matricula', 'institucion', 'telefono'],
        obligatorios: ['nombre', 'appa', 'apma', 'correo']
    },
    2: { // Admin
        campos: ['nombre', 'appa', 'apma', 'correo'],
        obligatorios: ['nombre', 'appa', 'apma', 'correo']
    },
    3: { // Tutor
        campos: ['nombre', 'appa', 'apma', 'fechaNacimiento', 'telefono', 'correo'],
        obligatorios: ['nombre', 'appa', 'apma', 'correo']
    }
};

const validators = {
    nombre: (v) => !v?.trim() || v.trim().length < 2 ? 'El nombre debe tener al menos 2 caracteres.' : '',
    appa: (v) => !v?.trim() || v.trim().length < 2 ? 'El apellido paterno debe tener al menos 2 caracteres.' : '',
    apma: (v) => !v?.trim() || v.trim().length < 2 ? 'El apellido materno debe tener al menos 2 caracteres.' : '',
    correo: (v) => {
        if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Debe ingresar un correo válido.';
        return v.length > 100 ? 'El correo no puede exceder 100 caracteres.' : '';
    },
    curp: (v) => v && v.length !== 18 ? 'El CURP debe tener exactamente 18 caracteres.' : '',
    telefono: (v) => v && (v.length < 10 || v.length > 14) ? 'El teléfono debe tener entre 10 y 14 dígitos.' : '',
    matricula: (v) => v && v.trim().length < 3 ? 'La matrícula debe tener al menos 3 caracteres.' : ''
};

const mapearCamposDatos = (formData, camposPermitidos) => {
    const datosUsuario = { usuario: {}, perfil: {} };
    
    camposPermitidos.forEach(campo => {
        const valor = formData[campo]?.trim() || null;
        
        if (['nombre', 'appa', 'apma', 'correo'].includes(campo)) {
            const key = campo === 'appa' ? 'apellido_paterno' : 
                        campo === 'apma' ? 'apellido_materno' : campo;
            datosUsuario.usuario[key] = valor;
        } else {
            const key = campo === 'fechaNacimiento' ? 'fecha_nacimiento' : campo;
            datosUsuario.perfil[key] = valor;
        }
    });
    
    return datosUsuario;
};

const EditarPerfil = ({ abrirModal, cerrarModal}) => {
    const { userData, getToken, updateUserData } = useAuth(); 
    const usuarioId = userData?.id || userData?.usuario_id; 
    const tipoUsuario = userData?.tipo; 
    const [formData, setFormData] = useState({
        nombre: '', appa: '', apma: '', correo: '', curp: '', fechaNacimiento: '', 
        estado: '', municipio: '', bachillerato: '', matricula: '', institucion: '', telefono: ''
    });
    const [tutorCorreo, setTutorCorreo] = useState('');
    const [isClosing, setIsClosing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errores, setErrores] = useState({});

    const estados = [
        { value: "Aguascalientes", label: "Aguascalientes" },
        { value: "Baja California", label: "Baja California" },
        { value: "Baja California Sur", label: "Baja California Sur" },
        { value: "Campeche", label: "Campeche" },
        { value: "Chiapas", label: "Chiapas" },
        { value: "Chihuahua", label: "Chihuahua" },
        { value: "Ciudad de México", label: "Ciudad de México" },
        { value: "Coahuila", label: "Coahuila" },
        { value: "Colima", label: "Colima" },
        { value: "Durango", label: "Durango" },
        { value: "Estado de México", label: "Estado de México" },
        { value: "Guanajuato", label: "Guanajuato" },
        { value: "Guerrero", label: "Guerrero" },
        { value: "Hidalgo", label: "Hidalgo" },
        { value: "Jalisco", label: "Jalisco" },
        { value: "Michoacán", label: "Michoacán" },
        { value: "Morelos", label: "Morelos" },
        { value: "Nayarit", label: "Nayarit" },
        { value: "Nuevo León", label: "Nuevo León" },
        { value: "Oaxaca", label: "Oaxaca" },
        { value: "Puebla", label: "Puebla" },
        { value: "Quintana Roo", label: "Quintana Roo" },
        { value: "San Luis Potosí", label: "San Luis Potosí" },
        { value: "Sinaloa", label: "Sinaloa" },
        { value: "Sonora", label: "Sonora" },
        { value: "Tabasco", label: "Tabasco" },
        { value: "Tamaulipas", label: "Tamaulipas" },
        { value: "Tlaxcala", label: "Tlaxcala" },
        { value: "Veracruz", label: "Veracruz" },
        { value: "Yucatán", label: "Yucatán" },
        { value: "Zacatecas", label: "Zacatecas" },
    ];

    const municipios = [
            { value: "Amealco De Bonfil", label: "Amealco de Bonfil" },
            { value: "Arroyo Seco", label: "Arroyo Seco" },
            { value: "Cadereyta De Montes", label: "Cadereyta de Montes" },
            { value: "Colón", label: "Colón" },
            { value: "Corregidora", label: "Corregidora" },
            { value: "El Marqués", label: "El Marqués" },
            { value: "Ezequiel Montes", label: "Ezequiel Montes" },
            { value: "Huimilpan", label: "Huimilpan" },
            { value: "Jalpan De Serra", label: "Jalpan de Serra" },
            { value: "Landa De Matamoros", label: "Landa de Matamoros" },
            { value: "Pedro Escobedo", label: "Pedro Escobedo" },
            { value: "Peñamiller", label: "Peñamiller" },
            { value: "Pinal De Amoles", label: "Pinal de Amoles" },
            { value: "Querétaro", label: "Querétaro" },
            { value: "San Joaquín", label: "San Joaquín" },
            { value: "San Juan del Río", label: "San Juan del Río" },
            { value: "Tequisquiapan", label: "Tequisquiapan" },
            { value: "Tolimán", label: "Tolimán" },
    ];

    const bachilleratos = [
            { value: "Academia Casa Hogar Sor Juana Inés De La Cruz A.C.", dataMunicipio: "Querétaro", label: "Academia Casa Hogar Sor Juana Inés De La Cruz A.C." },
            { value: "Alma Muriel", dataMunicipio: "Querétaro", label: "Alma Muriel" },
            { value: "Bachillerato Carl Rogers", dataMunicipio: "Querétaro", label: "Bachillerato Carl Rogers" },
            { value: "Bachillerato Colegio México Nuevo Campus Querétaro", dataMunicipio: "Querétaro", label: "Bachillerato Colegio México Nuevo Campus Querétaro" },
            { value: "Bachillerato de la Universidad de las Mujeres", dataMunicipio: "Querétaro", label: "Bachillerato de la Universidad de las Mujeres" },
            { value: "Bachillerato de la Universidad del Valle de México Querétaro", dataMunicipio: "Querétaro", label: "Bachillerato de la Universidad del Valle de México Querétaro" },
            { value: "Bachillerato Nuevo Continente Campus Querétaro", dataMunicipio: "Querétaro", label: "Bachillerato Nuevo Continente Campus Querétaro" },
            { value: "Bachillerato Omi San Juan del Río", dataMunicipio: "San Juan del Río", label: "Bachillerato Omi San Juan del Río" },
            { value: "Bachillerato Tecnológico de la Universidad Internacional de Querétaro", dataMunicipio: "Querétaro", label: "Bachillerato Tecnológico de la Universidad Internacional de Querétaro" },
            { value: "Bachillerato Tecnológico del Instituto de Estudios Turísticos del Centro", dataMunicipio: "Querétaro", label: "Bachillerato Tecnológico del Instituto de Estudios Turísticos del Centro" },

            {
                value: "Bachillerato Tecnológico del Instituto Juárez Lincoln"
                , dataMunicipio: "Querétaro", label: "Bachillerato Tecnológico del Instituto Juárez Lincoln"
            },
            {
                value: "Bachillerato Tecnológico del Instituto Uni de Querétaro"
                , dataMunicipio: "Querétaro", label: "Bachillerato Tecnológico del Instituto Uni de Querétaro"
            },
            {
                value: "Bachillerato Tecnológico Grupo Isima, Plantel Querétaro"
                , dataMunicipio: "Querétaro", label: "Bachillerato Tecnológico Grupo Isima, Plantel Querétaro"
            },
            {
                value: "Bachillerato Tecnológico Instituto Vasco Plantel Querétaro"
                , dataMunicipio: "Querétaro", label: "Bachillerato Tecnológico Instituto Vasco Plantel Querétaro"
            },
            { value: "Bachillerato UGM Querétaro", dataMunicipio: "Querétaro", label: "Bachillerato UGM Querétaro" },
            { value: "Bachillerato UNEA Campus Querétaro", dataMunicipio: "Querétaro", label: "Bachillerato UNEA Campus Querétaro" },
            { value: "Buckingham High School", dataMunicipio: "Querétaro", label: "Buckingham High School" },
            { value: "CAED CECATI 17", dataMunicipio: "Querétaro", label: "CAED CECATI 17" },
            { value: "CAED CECATI 175", dataMunicipio: "Querétaro", label: "CAED CECATI 175" },
            { value: "CAED Querétaro", dataMunicipio: "Querétaro", label: "CAED Querétaro" },

            { value: "CAED San Juan del Río", dataMunicipio: "San Juan del Río", label: "CAED San Juan del Río" },
            { value: "CEDART Ignacio Mariano de las Casas", dataMunicipio: "Querétaro", label: "CEDART Ignacio Mariano de las Casas" },
            { value: "CEDVA Preparatoria", dataMunicipio: "Querétaro", label: "CEDVA Preparatoria" },
            { value: "Centro Cultural Fray Junípero Serra", dataMunicipio: "Querétaro", label: "Centro Cultural Fray Junípero Serra" },
            {
                value: "Centro de Atención para Estudiantes con Discapacidad, CECATI 122"
                , dataMunicipio: "Querétaro", label: "Centro de Atención para Estudiantes con Discapacidad, CECATI 122"
            },
            { value: "Centro de Bachillerato Tecnológico Agropecuario No. 326, en El Cazadero, Querétaro", dataMunicipio: "Querétaro", label: "Centro de Bachillerato Tecnológico Agropecuario No. 326, en ElCazadero, Querétaro" },
            {
                value: "Centro de Bachillerato Tecnológico Agropecuario Num. 115"
                , dataMunicipio: "Querétaro", label: "Centro de Bachillerato Tecnológico Agropecuario Num. 115"
            },
            {
                value: "Centro de Bachillerato Tecnológico Agropecuario Num. 256"
                , dataMunicipio: "Querétaro", label: "Centro de Bachillerato Tecnológico Agropecuario Num. 256"
            },
            {
                value: "Centro de Bachillerato Tecnológico Industrial y de Servicios Num. 118"
                , dataMunicipio: "Corregidora", label: "Centro de Bachillerato Tecnológico Industrial y de Servicios Num. 118"
            },
            {
                value: "Centro de Bachillerato Tecnológico Industrial y de Servicios Num. 145"
                , dataMunicipio: "Querétaro", label: "Centro de Bachillerato Tecnológico Industrial y de Servicios Num. 145"
            },

            { value: "Centro de Educación y Formación Jamadi", dataMunicipio: "Querétaro", label: "Centro de Educación y Formación Jamadi" },
            { value: "Centro de Estudios de Bachillerato John Von Newman", dataMunicipio: "Querétaro", label: "Centro de Estudios de Bachillerato John Von Newman" },
            { value: "Centro de Estudios Medio Superiores y Superiores", dataMunicipio: "Querétaro", label: "Centro de Estudios Medio Superiores y Superiores" },
            {
                value: "Centro de Estudios Tecnológicos en Aguas Continentales No. 23 en El Marqués, Qro."
                , dataMunicipio: "El Marqués", label: "Centro de Estudios Tecnológicos en Aguas Continentales No. 23 en El Marqués, Qro."
            },
            {
                value: "Centro de Estudios Tecnológicos en Aguas Continentales No. 24 en Corregidora, Qro."
                , dataMunicipio: "Corregidora", label: "Centro de Estudios Tecnológicos en Aguas Continentales No. 24 en Corregidora, Qro."
            },
            {
                value: "Centro de Estudios Tecnológicos Industrial y de Servicios Num. 105"
                , dataMunicipio: "Querétaro", label: "Centro de Estudios Tecnológicos Industrial y de Servicios Num. 105"
            },
            {
                value: "Centro de Estudios Tecnológicos Industrial y de Servicios Num. 142"
                , dataMunicipio: "Querétaro", label: "Centro de Estudios Tecnológicos Industrial y de Servicios Num. 142"
            },
            {
                value: "Centro de Estudios Tecnológicos Industrial y de Servicios Num. 16"
                , dataMunicipio: "Querétaro", label: "Centro de Estudios Tecnológicos Industrial y de Servicios Num. 16"
            },
            { value: "Centro de Estudios Universitarios en Querétaro", dataMunicipio: "Querétaro", label: "Centro de Estudios Universitarios en Querétaro" },
            {
                value: "Centro de Formación de Recursos de Enfermería de Querétaro, A.C."
                , dataMunicipio: "Querétaro", label: "Centro de Formación de Recursos de Enfermería de Querétaro, A.C."
            },

            { value: "Centro de Innovación Educativa México", dataMunicipio: "Querétaro", label: "Centro de Innovación Educativa México" },
            {
                value: "Centro Interactivo de Educación y Alta Especialización CIEE"
                , dataMunicipio: "Querétaro", label: "Centro Interactivo de Educación y Alta Especialización CIEE"
            },
            { value: "Centro Unión", dataMunicipio: "Querétaro", label: "Centro Unión" },
            { value: "Centro Universitario de Educación Contemporánea", dataMunicipio: "Querétaro", label: "Centro Universitario de Educación Contemporánea" },
            {
                value: "Centro Universitario Salvador Vázquez Altamirano, A.C."
                , dataMunicipio: "Querétaro", label: "Centro Universitario Salvador Vázquez Altamirano, A.C."
            },
            { value: "Cinco de Mayo", dataMunicipio: "Querétaro", label: "Cinco de Mayo" },
            { value: "Clara Barton", dataMunicipio: "Querétaro", label: "Clara Barton" },
            { value: "Clemencia Borja Taboada", dataMunicipio: "Querétaro", label: "Clemencia Borja Taboada" },
            { value: "COBAQ Bachillerato Mixto", dataMunicipio: "Querétaro", label: "COBAQ Bachillerato Mixto" },
            { value: "COBAQ Plantel Num. 28 San Ildefonso", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 28 San Ildefonso" },

            { value: "COBAQ Plantel Num. 1 Satélite", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 1 Satélite" },
            { value: "COBAQ Plantel Num. 10 San Juan del Río", dataMunicipio: "San Juan del Río", label: "COBAQ Plantel Num. 10 San Juan del Río" },
            { value: "COBAQ Plantel Num. 11 Ezequiel Montes", dataMunicipio: "Ezequiel Montes", label: "COBAQ Plantel Num. 11 Ezequiel Montes" },
            { value: "COBAQ Plantel Num. 12 Tequisquiapan", dataMunicipio: "Tequisquiapan", label: "COBAQ Plantel Num. 12 Tequisquiapan" },
            { value: "COBAQ Plantel Num. 13 Epigmenio González", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 13 Epigmenio González" },
            { value: "COBAQ Plantel Num. 14 San Joaquín", dataMunicipio: "San Joaquín", label: "COBAQ Plantel Num. 14 San Joaquín" },
            { value: "COBAQ Plantel Num. 15 Chichimequillas", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 15 Chichimequillas" },
            { value: "COBAQ Plantel Num. 16 El Colorado", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 16 El Colorado" },
            { value: "COBAQ Plantel Num. 17 'Constitución de 1917'", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 17 'Constitución de 1917'" },
            { value: "COBAQ Plantel Num. 18 Valle Dorado", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 18 Valle Dorado" },

            { value: "COBAQ Plantel Num. 19 Bravo", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 19 Bravo" },
            { value: "COBAQ Plantel Num. 2 Amealco", dataMunicipio: "Amealco De Bonfil", label: "COBAQ Plantel Num. 2 Amealco" },
            { value: "COBAQ Plantel Num. 20 Santiago Mexquititlán", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 20 Santiago Mexquititlán" },
            { value: "COBAQ Plantel Num. 21 Arcila", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 21 Arcila" },
            { value: "COBAQ Plantel Num. 22 Real de San Miguel", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 22 Real de San Miguel" },
            { value: "COBAQ Plantel Num. 23 Ahuacatlán de Guadalupe", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 23 Ahuacatlán de Guadalupe" },
            { value: "COBAQ Plantel Num. 24 Villa Progreso", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 24 Villa Progreso" },
            { value: "COBAQ Plantel Num. 25 La Lagunita", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 25 La Lagunita" },
            { value: "COBAQ Plantel Num. 26 Agua Zarca", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 26 Agua Zarca" },
            { value: "COBAQ Plantel Num. 27 Purísima de Arista", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 27 Purísima de Arista" },

            { value: "COBAQ Plantel Num. 29 Bernal", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 29 Bernal" },
            { value: "COBAQ Plantel Num. 3 Corregidora", dataMunicipio: "Corregidora", label: "COBAQ Plantel Num. 3 Corregidora" },
            { value: "COBAQ Plantel Num. 30 La Valla", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 30 La Valla" },
            { value: "COBAQ Plantel Num. 31 La Griega", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 31 La Griega" },
            { value: "COBAQ Plantel Num. 32 San Antonio de la Cal", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 32 San Antonio de la Cal" },
            { value: "COBAQ Plantel Num. 33 El Palmar", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 33 El Palmar" },
            { value: "COBAQ Plantel Num. 34 Arroyo Seco", dataMunicipio: "Arroyo Seco", label: "COBAQ Plantel Num. 34 Arroyo Seco" },
            { value: "COBAQ Plantel Num. 35 Vizarrón", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 35 Vizarrón" },
            { value: "COBAQ Plantel Num. 4 Jalpan", dataMunicipio: "Jalpan de Serra", label: "COBAQ Plantel Num. 4 Jalpan" },
            { value: "COBAQ Plantel Num. 5 Cadereyta", dataMunicipio: "Cadereyta de Montes", label: "COBAQ Plantel Num. 5 Cadereyta" },

            { value: "COBAQ Plantel Num. 6 Tolimán", dataMunicipio: "Tolimán", label: "COBAQ Plantel Num. 6 Tolimán" },
            { value: "COBAQ Plantel Num. 7 El Marqués", dataMunicipio: "El Marqués", label: "COBAQ Plantel Num. 7 El Marqués" },
            { value: "COBAQ Plantel Num. 8 Azteca", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 8 Azteca" },
            { value: "COBAQ Plantel Num. 9 Santa Rosa Jáuregui", dataMunicipio: "Querétaro", label: "COBAQ Plantel Num. 9 Santa Rosa Jáuregui" },
            { value: "Colegio Álamos", dataMunicipio: "Querétaro", label: "Colegio Álamos" },
            { value: "Colegio Austriaco Mexicano Preparatoria", dataMunicipio: "Querétaro", label: "Colegio Austriaco Mexicano Preparatoria" },
            { value: "Colegio Bosques Preparatoria Campus Real", dataMunicipio: "Querétaro", label: "Colegio Bosques Preparatoria Campus Real" },
            { value: "Colegio Carol Baur", dataMunicipio: "Querétaro", label: "Colegio Carol Baur" },
            { value: "Colegio Castellano Preparatoria", dataMunicipio: "Querétaro", label: "Colegio Castellano Preparatoria" },
            { value: "Colegio Celta Internacional", dataMunicipio: "Querétaro", label: "Colegio Celta Internacional" },

            { value: "Colegio Cervantes", dataMunicipio: "Querétaro", label: "Colegio Cervantes" },
            { value: "Colegio de Ciencias y Humanidades de Querétaro", dataMunicipio: "Querétaro", label: "Colegio de Ciencias y Humanidades de Querétaro" },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 10 Plantel La Esperanza"
                , dataMunicipio: "Querétaro", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 10 Plantel La Esperanza"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 11 Plantel La Tinaja",
                dataMunicipio: "Querétaro", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 11 Plantel La Tinaja"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 12 Plantel Paso de Mata"
                , dataMunicipio: "Querétaro", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 12 Plantel Paso de Mata"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 6 Corregidora"
                , dataMunicipio: "Corregidora", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 6 Corregidora"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 7 Plantel San Juan del Río"
                , dataMunicipio: "San Juan del Río", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 7 Plantel San Juan del Río"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 8 Plantel Menchaca"
                , dataMunicipio: "Querétaro", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 8 Plantel Menchaca"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 82 Plantel Huimilpan"
                , dataMunicipio: "Huimilpan", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 82 Plantel Huimilpan"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 83 Plantel Pedro Escobedo"
                , dataMunicipio: "Pedro Escobedo", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 83 Plantel Pedro Escobedo"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 84 Plantel Pinal de Amoles"
                , dataMunicipio: "Pinal De Amoles", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 84 Plantel Pinal de Amoles"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 85 Plantel Peñamiller"
                , dataMunicipio: "Peñamiller", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 85 Plantel Peñamiller"
            },
            {
                value: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 9 Plantel Montenegro"
                , dataMunicipio: "Querétaro", label: "Colegio de Estudios Científicos y Tecnológicos del Estado de Querétaro Num. 9 Plantel Montenegro"
            },
            {
                value: "Colegio de Estudios Cientificos y Tecnológicos del Estado de Querétaro Plantel Querétaro"
                , dataMunicipio: "Querétaro", label: "Colegio de Estudios Cientificos y Tecnológicos del Estado de Querétaro Plantel Querétaro"
            },
            { value: "Colegio de Estudios Profesionales Lausanne", dataMunicipio: "Querétaro", label: "Colegio de Estudios Profesionales Lausanne" },
            { value: "Colegio del Tepeyac, Querétaro", municipio: "Querétaro", label: "Colegio del Tepeyac, Querétaro" },
            { value: "Colegio Diego Olvera Estrada", dataMunicipio: "Querétaro", label: "Colegio Diego Olvera Estrada" },
            { value: "Colegio Educación y Patria", dataMunicipio: "Querétaro", label: "Colegio Educación y Patria" },
            { value: "Colegio Fontanar", dataMunicipio: "Querétaro", label: "Colegio Fontanar" },
            { value: "Colegio Fray Luis de León", dataMunicipio: "Querétaro", label: "Colegio Fray Luis de León" },

            { value: "Colegio General Regules", dataMunicipio: "Querétaro", label: "Colegio General Regules" },
            { value: "Colegio Gran Bretaña", dataMunicipio: "Querétaro", label: "Colegio Gran Bretaña" },
            { value: "Colegio Helen Parkhurst", dataMunicipio: "Querétaro", label: "Colegio Helen Parkhurst" },
            { value: "Colegio Holandés de Querétaro", dataMunicipio: "Querétaro", label: "Colegio Holandés de Querétaro" },
            { value: "Colegio Internacional de Estudios Virtuales", dataMunicipio: "Querétaro", label: "Colegio Internacional de Estudios Virtuales" },
            { value: "Colegio Internacional de Querétaro", dataMunicipio: "Querétaro", label: "Colegio Internacional de Querétaro" },
            { value: "Colegio La Salle San Juan del Río", dataMunicipio: "San Juan del Río", label: "Colegio La Salle San Juan del Río" },
            { value: "Colegio Lafayette de Querétaro, A. C.", dataMunicipio: "Querétaro", label: "Colegio Lafayette de Querétaro, A. C." },
            { value: "Colegio Marcelina", dataMunicipio: "Querétaro", label: "Colegio Marcelina" },
            { value: "Colegio Masía de Querétaro", dataMunicipio: "Querétaro", label: "Colegio Masía de Querétaro" },

            { value: "Colegio Militarizado Nuevo México Campus Querétaro", dataMunicipio: "Querétaro", label: "Colegio Militarizado Nuevo México Campus Querétaro" },
            { value: "Colegio Montpellier", dataMunicipio: "Querétaro", label: "Colegio Montpellier" },
            { value: "Colegio Motolínia de Sistema Educativo Valladolid", dataMunicipio: "Querétaro", label: "Colegio Motolínia de Sistema Educativo Valladolid" },
            { value: "Colegio Nacional de Danza Contemporánea", dataMunicipio: "Querétaro", label: "Colegio Nacional de Danza Contemporánea" },
            {
                value: "Colegio Nacional de Educación Profesional Técnica 'Amealco'"
                , dataMunicipio: "Amealco De Bonfil", label: "Colegio Nacional de Educación Profesional Técnica 'Amealco'"
            },
            {
                value: "Colegio Nacional de Educación Profesional Técnica Num. 41 'Querétaro'"
                , dataMunicipio: "Querétaro", label: "Colegio Nacional de Educación Profesional Técnica Num. 41 'Querétaro'"
            },
            {
                value: "Colegio Nacional de Educación Profesional Técnica Num. 42 'San Juan del Río'"
                , dataMunicipio: "San Juan del Río", label: "Colegio Nacional de Educación Profesional Técnica Num. 42 'San Juan del Río'"
            },
            { value: "Colegio Nueva España", dataMunicipio: "Querétaro", label: "Colegio Nueva España" },
            { value: "Colegio Real de Querenda Bachillerato", dataMunicipio: "Querétaro", label: "Colegio Real de Querenda Bachillerato" },
            { value: "Colegio South Hill de Querétaro, S.C.", dataMunicipio: "Querétaro", label: "Colegio South Hill de Querétaro, S.C." },
            { value: "Colegio Suizo de México", dataMunicipio: "Querétaro", label: "Colegio Suizo de México" },
            { value: "Colegio Sunhills Valley", dataMunicipio: "Querétaro", label: "Colegio Sunhills Valley" },
            { value: "Colegio Universitario de Humanidades (CUDH)", dataMunicipio: "Querétaro", label: "Colegio Universitario de Humanidades (CUDH)" },
            { value: "Colegio Wexford", dataMunicipio: "Querétaro", label: "Colegio Wexford" },
            {
                value: "Conjunto Educativo Social Comercial Administrativo, A.C."
                , dataMunicipio: "Querétaro", label: "Conjunto Educativo Social Comercial Administrativo, A.C."
            },
            { value: "Coordinación de Preparatoria Abierta", dataMunicipio: "Querétaro", label: "Coordinación de Preparatoria Abierta" },
            { value: "Corregidora", dataMunicipio: "Querétaro", label: "Corregidora" },
            {
                value: "Educación Media Superior a Distancia Num. 11 Peña Colorada"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 11 Peña Colorada"
            },
            { value: "Educación Media Superior a Distancia Num. 12 Camargo", dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 12 Camargo" },
            {
                value: "Educación Media Superior a Distancia Num. 13 San Pedro Escanela"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 13 San Pedro Escanela"
            },

            {
                value: "Educación Media Superior a Distancia Num. 14 Tres Lagunas"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 14 Tres Lagunas"
            },
            { value: "Educación Media Superior a Distancia Num. 18 Tilaco", dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 18 Tilaco" },
            {
                value: "Educación Media Superior a Distancia Num. 19 San Miguel Palmas"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 19 San Miguel Palmas"
            },
            { value: "Educación Media Superior a Distancia Num. 3 Conca", dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 3 Conca" },
            {
                value: "Educación Media Superior a Distancia Num. 31 Valle Verde"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 31 Valle Verde"
            },
            {
                value: "Educación Media Superior a Distancia Num. 32 Santa María Álamos"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 32 Santa María Álamos"
            },
            {
                value: "Educación Media Superior a Distancia Num. 33 Buenavista"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 33 Buenavista"
            },
            {
                value: "Educación Media Superior a Distancia Num. 34 Escolásticas"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 34 Escolásticas"
            },
            {
                value: "Educación Media Superior a Distancia Num. 6 El Coyote"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 6 El Coyote"
            },
            {
                value: "Educación Media Superior a Distancia Num. 7 Higuerillas"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 7 Higuerillas"
            },

            {
                value: "Educación Media Superior a Distancia Num. 8 Santa Águeda"
                , dataMunicipio: "Querétaro", label: "Educación Media Superior a Distancia Num. 8 Santa Águeda"
            },
            { value: "Educativa Metropolitana", dataMunicipio: "Querétaro", label: "Educativa Metropolitana" },
            { value: "EMSAD Num. 20 Landa de Matamoros", dataMunicipio: "Landa De Matamoros", label: "EMSAD Num. 20 Landa de Matamoros" },
            { value: "EMSAD Num. 22 Peña Blanca", dataMunicipio: "Querétaro", label: "EMSAD Num. 22 Peña Blanca" },
            { value: "EMSAD Num. 23 Sombrerete", dataMunicipio: "Querétaro", label: "EMSAD Num. 23 Sombrerete" },
            { value: "EMSAD Num. 24 Casa Blanca", dataMunicipio: "Querétaro", label: "EMSAD Num. 24 Casa Blanca" },
            { value: "EMSAD Num. 25 El Rincón", dataMunicipio: "Querétaro", label: "EMSAD Num. 25 El Rincón" },
            { value: "EMSAD Num. 26 Tancoyol", dataMunicipio: "Querétaro", label: "EMSAD Num. 26 Tancoyol" },
            { value: "EMSAD Num. 27 Agua Fría", dataMunicipio: "Querétaro", label: "EMSAD Num. 27 Agua Fría" },
            { value: "EMSAD Num. 28 Bellavista del Río", dataMunicipio: "Querétaro", label: "EMSAD Num. 28 Bellavista del Río" },

            { value: "EMSAD Num. 30 San Miguel Tlaxcaltepec", dataMunicipio: "Querétaro", label: "EMSAD Num. 30 San Miguel Tlaxcaltepec" },
            { value: "Escuela de Asistentes Educativos Chiquitines, A. C.", dataMunicipio: "Querétaro", label: "Escuela de Asistentes Educativos Chiquitines, A. C." },
            {
                value: "Escuela de Bachilleres de la UAQ, Modalidad Mixta y no Escolarizada"
                , dataMunicipio: "Querétaro", label: "Escuela de Bachilleres de la UAQ, Modalidad Mixta y no Escolarizada"
            },
            { value: "Escuela de Bachilleres de la UAQ, Plantel Amazcala", dataMunicipio: "Querétaro", label: "Escuela de Bachilleres de la UAQ, Plantel Amazcala" },
            {
                value: "Escuela de Bachilleres de la UAQ, Plantel Amealco"
                , dataMunicipio: "Amealco De Bonfil", label: "Escuela de Bachilleres de la UAQ, Plantel Amealco"
            },
            {
                value: "Escuela de Bachilleres de la UAQ, Plantel Bicentenario"
                , dataMunicipio: "Querétaro", label: "Escuela de Bachilleres de la UAQ, Plantel Bicentenario"
            },
            { value: "Escuela de Bachilleres de la UAQ, Plantel Colón", dataMunicipio: "Colón", label: "Escuela de Bachilleres de la UAQ, Plantel Colón" },
            { value: "Escuela de Bachilleres de la UAQ, Plantel Conca", dataMunicipio: "Querétaro", label: "Escuela de Bachilleres de la UAQ, Plantel Conca" },
            {
                value: "Escuela de Bachilleres de la UAQ, Plantel Corregidora"
                , dataMunicipio: "Corregidora", label: "Escuela de Bachilleres de la UAQ, Plantel Corregidora"
            },
            { value: "Escuela de Bachilleres de la UAQ, Plantel Huimilpan", dataMunicipio: "Huimilpan", label: "Escuela de Bachilleres de la UAQ, Plantel Huimilpan" },

            {
                value: "Escuela de Bachilleres de la UAQ, Plantel Jalpan"
                , dataMunicipio: "Jalpan de Serra", label: "Escuela de Bachilleres de la UAQ, Plantel Jalpan"
            },
            {
                value: "Escuela de Bachilleres de la UAQ, Plantel Landa de Matamoros"
                , dataMunicipio: "Landa De Matamoros", label: "Escuela de Bachilleres de la UAQ, Plantel Landa de Matamoros"
            },
            { value: "Escuela de Bachilleres de la UAQ, Plantel Norte", dataMunicipio: "Querétaro", label: "Escuela de Bachilleres de la UAQ, Plantel Norte" },
            {
                value: "Escuela de Bachilleres de la UAQ, Plantel Pedro Escobedo"
                , dataMunicipio: "Pedro Escobedo", label: "Escuela de Bachilleres de la UAQ, Plantel Pedro Escobedo"
            },
            {
                value: "Escuela de Bachilleres de la UAQ, Plantel Pinal de Amoles"
                , dataMunicipio: "Pinal De Amoles", label: "Escuela de Bachilleres de la UAQ, Plantel Pinal de Amoles"
            },
            {
                value: "Escuela de Bachilleres de la UAQ, Plantel San Juan del Río"
                , dataMunicipio: "San Juan del Río", label: "Escuela de Bachilleres de la UAQ, Plantel San Juan del Río"
            },
            { value: "Escuela de Bachilleres de la UAQ, Plantel Sur", dataMunicipio: "Querétaro", label: "Escuela de Bachilleres de la UAQ, Plantel Sur" },
            { value: "Escuela Preparatoria del Instituto Plancarte", dataMunicipio: "Querétaro", label: "Escuela Preparatoria del Instituto Plancarte" },
            { value: "Escuela Preparatoria Leonardo de Vinci", dataMunicipio: "Querétaro", label: "Escuela Preparatoria Leonardo de Vinci" },
            { value: "High School John Dewey", dataMunicipio: "Querétaro", label: "High School John Dewey" },

            { value: "Instituto Bilingüe Victoria, A. C.", dataMunicipio: "Querétaro", label: "Instituto Bilingüe Victoria, A. C." },
            { value: "Instituto Cambridge", dataMunicipio: "Querétaro", label: "Instituto Cambridge" },
            { value: "Instituto Champagnat", dataMunicipio: "Querétaro", label: "Instituto Champagnat" },
            { value: "Instituto Comercial Imec", dataMunicipio: "Querétaro", label: "Instituto Comercial Imec" },
            {
                value: "Instituto de Estudios Sociales y Administrativos de Querétaro"
                , dataMunicipio: "Querétaro", label: "Instituto de Estudios Sociales y Administrativos de Querétaro"
            },
            { value: "Instituto de la Juventud José Martí", dataMunicipio: "Querétaro", label: "Instituto de la Juventud José Martí" },
            { value: "Instituto de Liderazgo Idel", dataMunicipio: "Querétaro", label: "Instituto de Liderazgo Idel" },
            { value: "Instituto Ex Novo", dataMunicipio: "Querétaro", label: "Instituto Ex Novo" },
            { value: "Instituto La Paz de Querétaro, A.C.", dataMunicipio: "Querétaro", label: "Instituto La Paz de Querétaro, A.C." },
            { value: "Instituto Marciano Tinajero y Estrada", dataMunicipio: "Querétaro", label: "Instituto Marciano Tinajero y Estrada" },

            { value: "Instituto Oriente Querétaro", dataMunicipio: "Querétaro", label: "Instituto Oriente Querétaro" },
            { value: "Instituto Paulo Freire Preparatoria", dataMunicipio: "Querétaro", label: "Instituto Paulo Freire Preparatoria" },
            { value: "Instituto Plancarte San Juan del Río", dataMunicipio: "San Juan del Río", label: "Instituto Plancarte San Juan del Río" },
            {
                value: "Instituto Preuniversitario del Centro/body> de México Campus Querétaro"
                , dataMunicipio: "Querétaro", label: "Instituto Preuniversitario del Centro de México Campus Querétaro"
            },
            {
                value: "Instituto Preuniversitario del Centro de México Campus San Juan del Río"
                , dataMunicipio: "San Juan del Río", label: "Instituto Preuniversitario del Centro de México Campus San Juan del Río"
            },
            { value: "Instituto Profesional de Informática y Computación", dataMunicipio: "Querétaro", label: "Instituto Profesional de Informática y Computación" },
            { value: "Instituto Querétano", dataMunicipio: "Querétaro", label: "Instituto Querétano" },
            { value: "Instituto Querétano de Herramentales", dataMunicipio: "Querétaro", label: "Instituto Querétano de Herramentales" },
            { value: "Instituto Rembrandt", dataMunicipio: "Querétaro", label: "Instituto Rembrandt" },
            { value: "Instituto Renacimiento Preparatoria", dataMunicipio: "Querétaro", label: "Instituto Renacimiento Preparatoria" },

            { value: "Instituto Santiago (Preparatoria)", dataMunicipio: "Querétaro", label: "Instituto Santiago (Preparatoria)" },
            {
                value: "Instituto Social Comercial en Contaduría Administrativa"
                , dataMunicipio: "Querétaro", label: "Instituto Social Comercial en Contaduría Administrativa"
            },
            {
                value: "Instituto Social Comercial en Contaduría Administrativa A.C."
                , dataMunicipio: "Querétaro", label: "Instituto Social Comercial en Contaduría Administrativa A.C."
            },
            { value: "Instituto Social Educativo Comercial Administrativo", dataMunicipio: "Querétaro", label: "Instituto Social Educativo Comercial Administrativo" },
            { value: "Instituto Sovany", dataMunicipio: "Querétaro", label: "Instituto Sovany" },
            { value: "Instituto Tecnológico Anglo Mexicano", dataMunicipio: "Querétaro", label: "Instituto Tecnológico Anglo Mexicano" },
            { value: "Instituto Thomas Jefferson", dataMunicipio: "Querétaro", label: "Instituto Thomas Jefferson" },
            {
                value: "Instituto Universitario de Desarrollo Humano e Innovación Profesional"
                , dataMunicipio: "Querétaro", label: "Instituto Universitario de Desarrollo Humano e Innovación Profesional"
            },
            { value: "ITESM Campus Querétaro", dataMunicipio: "Querétaro", label: "ITESM Campus Querétaro" },
            { value: "John F. Kennedy", dataMunicipio: "Querétaro", label: "John F. Kennedy" },

            { value: "Liceo de Querétaro", dataMunicipio: "Querétaro", label: "Liceo de Querétaro" },
            { value: "Lic. José Guadalupe Ramírez Álvarez", dataMunicipio: "Querétaro", label: "Lic. José Guadalupe Ramírez Álvarez" },
            { value: "Liceo Corregidora", dataMunicipio: "Corregidora", label: "Liceo Corregidora" },
            { value: "Liceo Patrick Henry Preparatoria", dataMunicipio: "Querétaro", label: "Liceo Patrick Henry Preparatoria" },
            {
                value: "Lycée Français International Jules Verne Sección Preparatoria"
                , dataMunicipio: "Querétaro", label: "Lycée Français International Jules Verne Sección Preparatoria"
            },
            { value: "MH Institución Educativa", dataMunicipio: "Querétaro", label: "MH Institución Educativa" },
            { value: "New Port High School", dataMunicipio: "Querétaro", label: "New Port High School" },
            { value: "Plantel Conalep Aeronáutico", dataMunicipio: "Querétaro", label: "Plantel Conalep Aeronáutico" },
            { value: "Plantel Conalep Automotriz José Vasconcelos", dataMunicipio: "Querétaro", label: "Plantel Conalep Automotriz José Vasconcelos" },
            { value: "Prepa Anáhuac Querétaro", dataMunicipio: "Querétaro", label: "Prepa Anáhuac Querétaro" },

            { value: "Prepa Balmoral Escocés", dataMunicipio: "Querétaro", label: "Prepa Balmoral Escocés" },
            { value: "Prepa Cav", dataMunicipio: "Querétaro", label: "Prepa Cav" },
            { value: "Prepa Ceickor", dataMunicipio: "Querétaro", label: "Prepa Ceickor" },
            { value: "Prepa Idai", dataMunicipio: "Querétaro", label: "Prepa Idai" },
            { value: "Prepa Plenus", dataMunicipio: "Querétaro", label: "Prepa Plenus" },
            { value: "Preparatoria Centro Escolar Cadereyta", dataMunicipio: "Cadereyta de Montes", label: "Preparatoria Centro Escolar Cadereyta" },
            { value: "Preparatoria Cervantes", dataMunicipio: "Querétaro", label: "Preparatoria Cervantes" },
            { value: "Preparatoria Cesba", dataMunicipio: "Querétaro", label: "Preparatoria Cesba" },
            { value: "Preparatoria Conín", dataMunicipio: "Querétaro", label: "Preparatoria Conín" },
            { value: "Preparatoria Cuauhtémoc Querétaro", dataMunicipio: "Querétaro", label: "Preparatoria Cuauhtémoc Querétaro" },

            { value: "Preparatoria de Negocios", dataMunicipio: "Querétaro", label: "Preparatoria de Negocios" },
            { value: "Preparatoria del Instituto Asunción de Querétaro", dataMunicipio: "Querétaro", label: "Preparatoria del Instituto Asunción de Querétaro" },
            { value: "Preparatoria Emprende Innova", dataMunicipio: "Querétaro", label: "Preparatoria Emprende Innova" },
            { value: "Preparatoria Intercultural San Patricio", dataMunicipio: "Querétaro", label: "Preparatoria Intercultural San Patricio" },
            { value: "Preparatoria Iteca", dataMunicipio: "Querétaro", label: "Preparatoria Iteca" },
            { value: "Preparatoria La Cima", dataMunicipio: "Querétaro", label: "Preparatoria La Cima" },
            { value: "Preparatoria Nuevo Horizonte", dataMunicipio: "Querétaro", label: "Preparatoria Nuevo Horizonte" },
            { value: "Preparatoria Summerhill", dataMunicipio: "Querétaro", label: "Preparatoria Summerhill" },
            {
                value: "Preparatoria Universidad Marista de Querétaro, Campus San Juan del Río"
                , dataMunicipio: "San Juan del Río", label: "Preparatoria Universidad Marista de Querétaro, Campus San Juan del Río"
            },
            { value: "Preparatorias Universidad Marista de Querétaro", dataMunicipio: "Querétaro", label: "Preparatorias Universidad Marista de Querétaro" },

            { value: "Santiago Galás", dataMunicipio: "Querétaro", label: "Santiago Galás" },
            { value: "Telebachillerato Comunitario Agua Fría", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Agua Fría" },
            { value: "Telebachillerato Comunitario El Aguacate", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario El Aguacate" },
            { value: "Telebachillerato Comunitario El Lobo", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario El Lobo" },
            { value: "Telebachillerato Comunitario El Platano", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario El Platano" },
            { value: "Telebachillerato Comunitario La Carbonera", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario La Carbonera" },
            { value: "Telebachillerato Comunitario La Florida", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario La Florida" },
            { value: "Telebachillerato Comunitario La Fuente", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario La Fuente" },
            { value: "Telebachillerato Comunitario La Monja", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario La Monja" },
            { value: "Telebachillerato Comunitario La Plazuela", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario La Plazuela" },

            { value: "Telebachillerato Comunitario La Solana", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario La Solana" },
            { value: "Telebachillerato Comunitario La Versolilla", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario La Versolilla" },
            { value: "Telebachillerato Comunitario Laguna de Servín", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Laguna de Servín" },
            { value: "Telebachillerato Comunitario Laguna de Vaquerías", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Laguna de Vaquerías" },
            { value: "Telebachillerato Comunitario Los Juárez", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Los Juárez" },
            { value: "Telebachillerato Comunitario Maconí", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Maconí" },
            { value: "Telebachillerato Comunitario Mesa de Ramírez", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Mesa de Ramírez" },
            { value: "Telebachillerato Comunitario Neblinas", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Neblinas" },
            { value: "Telebachillerato Comunitario Paniagua", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Paniagua" },
            { value: "Telebachillerato Comunitario Pathe", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Pathe" },

            { value: "Telebachillerato Comunitario Puerto de la Luz", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Puerto de la Luz" },
            { value: "Telebachillerato Comunitario Quiotillos", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Quiotillos" },
            { value: "Telebachillerato Comunitario Rancho Nuevo", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Rancho Nuevo" },
            { value: "Telebachillerato Comunitario San Ignacio", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario San Ignacio" },
            { value: "Telebachillerato Comunitario San Pedro Viejo", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario San Pedro Viejo" },
            { value: "Telebachillerato Comunitario Santa Inés", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Santa Inés" },
            { value: "Telebachillerato Comunitario Santa Lucía", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Santa Lucía" },
            {
                value: "Telebachillerato Comunitario Santa María de los Cocos"
                , dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Santa María de los Cocos"
            },
            { value: "Telebachillerato Comunitario Sauz de Guadalupe", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Sauz de Guadalupe" },
            { value: "Telebachillerato Comunitario Yerbabuena", dataMunicipio: "Querétaro", label: "Telebachillerato Comunitario Yerbabuena" },

            { value: "Universidad Alfred Nobel Bachillerato", dataMunicipio: "Querétaro", label: "Universidad Alfred Nobel Bachillerato" },
            { value: "Universidad Mondragón México", dataMunicipio: "Querétaro", label: "Universidad Mondragón México" },
            { value: "Universidad Tec Milenio", dataMunicipio: "Querétaro", label: "Universidad Tec Milenio" },
            {
                value: "Universidad Tecnologica de Mexico Bachillerato, Campus Queretaro"
                , dataMunicipio: "Querétaro", label: "Universidad Tecnologica de Mexico Bachillerato, Campus Queretaro"
            },
            { value: "Vanguardia Educativa de Mexico", dataMunicipio: "Querétaro", label: "Vanguardia Educativa de Mexico" },
            { value: "Quiotillos 22ETK0027H", dataMunicipio: "Amealco De Bonfil", label: "Quiotillos 22ETK0027H" },
            { value: "Laguna De Servin 22ETK0028G", dataMunicipio: "Amealco De Bonfil", label: "Laguna De Servin 22ETK0028G" },
            { value: "Santa Maria De Los Cocos 22ETK0025J", dataMunicipio: "Arroyo Seco", label: "Santa Maria De Los Cocos 22ETK0025J" },
            { value: "La Florida 22ETK0026I", dataMunicipio: "Arroyo Seco", label: "La Florida 22ETK0026I" },
            { value: "El Aguacate 22ETK0006V", dataMunicipio: "Cadereyta De Montes", label: "El Aguacate 22ETK0006V" },
            { value: "Puerto De La Luz 22ETK0008T", dataMunicipio: "Cadereyta De Montes", label: "Puerto De La Luz 22ETK0008T " },
            { value: "Pathe 22ETK0009S", dataMunicipio: "Cadereyta De Montes", label: "Pathe 22ETK0009S" },
            { value: "Maconi 22ETK0023L", dataMunicipio: "Cadereyta De Montes", label: "Maconi 22ETK0023L" },
            { value: "Los Juarez 22ETK0024K", dataMunicipio: "Cadereyta De Montes", label: "Los Juarez 22ETK0024K" },
            { value: "La Carbonera 22ETK0007U", dataMunicipio: "Colón", label: "La Carbonera 22ETK0007U" },
            { value: "Paniagua 22ETK0017A", dataMunicipio: "Huimilpan", label: "Paniagua 22ETK0017A" },
            { value: "San Ignacio 22ETK0018Z", dataMunicipio: "Huimilpan", label: "San Ignacio 22ETK0018Z" },
            { value: "Agua Fria 22ETK0013E", dataMunicipio: "Jalpan De Serra", label: "Agua Fria 22ETK0013E" },
            { value: "Yerbabuena 22ETK0014D", dataMunicipio: "Jalpan De Serra", label: "Yerbabuena 22ETK0014D" },
            { value: "Neblinas 22ETK0012F", dataMunicipio: "Landa De Matamoros", label: "Neblinas 22ETK0012F" },
            { value: "Santa Ines 22ETK0015C", dataMunicipio: "Landa De Matamoros", label: "Santa Ines 22ETK0015C" },
            { value: "El Lobo 22ETK0016B", dataMunicipio: "Landa De Matamoros", label: "El Lobo 22ETK0016B" },
            { value: "La Plazuela 22ETK0021M", dataMunicipio: "Peñamiller", label: "La Plazuela 22ETK0021M" },
            { value: "Sauz De Guadalupe 22ETK0021N", dataMunicipio: "Pinal De Amoles", label: "Sauz De Guadalupe 22ETK0021N" },
            { value: "San Pedro Viejo 22ETK0022M", dataMunicipio: "Pinal De Amoles", label: "San Pedro Viejo 22ETK0022M" },
            { value: "La Versolilla 22ETK0022N", dataMunicipio: "Querétaro", label: "La Versolilla 22ETK0022N" },
            { value: "La Solana 22ETK0022O", dataMunicipio: "Querétaro", label: "La Solana 22ETK0022O" },
            { value: "La Monja 22ETK0020K", dataMunicipio: "San Joaquín", label: "La Monja 22ETK0020K" },
            { value: "El Plátano 22ETK0020L", dataMunicipio: "San Joaquín", label: "El Plátano 22ETK0020L" },
            { value: "Laguna De Vaquerias 22ETK0020M", dataMunicipio: "San Juan Del Río", label: "Laguna De Vaquerias 22ETK0020M" },
            { value: "Santa Lucia 22ETK0020N", dataMunicipio: "San Juan Del Río", label: "Santa Lucia 22ETK0020N" },
            { value: "La Fuente 22ETK0020O", dataMunicipio: "Tequisquiapan", label: "La Fuente 22ETK0020O" },
            { value: "Las Mesas De Ramirez 22ETK0020P", dataMunicipio: "Tolimán", label: "Las Mesas De Ramirez 22ETK0020P" },
            { value: "Rancho Nuevo 22ETK0019Z", dataMunicipio: "Tolimán", label: "Rancho Nuevo 22ETK0019Z" },
            { value: "Otro", label: "Otro" },
    ];

    const bachilleratosMunicipio = useMemo(() => {
        if (!formData.municipio) return [];
        return bachilleratos.filter(bachillerato => 
            bachillerato.dataMunicipio === formData.municipio
        );
    }, [formData.municipio]);

    const userConfig = useMemo(() => USER_CONFIG[tipoUsuario] || { campos: [], obligatorios: [] }, [tipoUsuario]);

    const mostrarAlerta = useCallback((tipo, titulo, texto = '', opciones = {}) => {
        return Swal.fire({
            title: titulo,
            text: texto,
            icon: tipo,
            confirmButtonText: tipo === 'error' ? 'Entendido' : 'Continuar',
            confirmButtonColor: '#242b57',
            customClass: { popup: 'popupbonito' },
            ...opciones
        });
    }, []);

    const realizarPeticion = useCallback(async (url, opciones = {}) => {
        const token = getToken();
        if (!token) throw new Error('No se encontró token de autenticación');
        
        const config = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...opciones.headers
            },
            ...opciones
        };

        const response = await fetch(url, config);
        
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (response.status === 401) throw new Error('Token inválido o expirado');
            if (response.status === 403) throw new Error('Sin permisos para acceder');
            throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
        }
        
        return response.json();
    }, [getToken]);

    const limpiarFormulario = useCallback(() => {
        setFormData({
            nombre: '', appa: '', apma: '', correo: '', curp: '', fechaNacimiento: '', 
            estado: '', municipio: '', bachillerato: '', matricula: '', institucion: '', telefono: ''
        });
        setTutorCorreo('');
        setErrores({});
    }, []);

    const cargarDatosUsuario = useCallback(async () => {
        if (userData) {
            const newFormData = {};
            userConfig.campos.forEach(campo => {
                const key = campo === 'appa' ? 'apellido_paterno' : 
                            campo === 'apma' ? 'apellido_materno' : 
                            campo === 'fechaNacimiento' ? 'fecha_nacimiento' : campo;
                
                if (campo === 'fechaNacimiento' && userData[key]) {
                    const fecha = new Date(userData[key]);
                    if (!isNaN(fecha.getTime())) {
                        newFormData[campo] = fecha.toISOString().split('T')[0];
                    } else {
                        newFormData[campo] = '';
                    }
                } else {
                    newFormData[campo] = userData[key] || '';
                }
            });
            
            setFormData(prev => ({ ...prev, ...newFormData }));
            
            if (tipoUsuario === 1 && userData.correo_tutor) {
                setTutorCorreo(userData.correo_tutor);
            }
            return;
        }
    }, [userData, userConfig.campos, tipoUsuario]);

    useEffect(() => {
        if (abrirModal) {
            cargarDatosUsuario();
        } else if (!isClosing) {
            limpiarFormulario();
        }
    }, [abrirModal, isClosing, cargarDatosUsuario, limpiarFormulario]);

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                cerrarModal();
                setIsClosing(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    const handleInputChange = useCallback((field, value) => {
        if (!userConfig.campos.includes(field)) return;
        
        setFormData(prev => ({ ...prev, [field]: value }));
        
        if (validators[field]) {
            const error = validators[field](value);
            setErrores(prev => ({ ...prev, [field]: error }));
        }
    }, [userConfig.campos]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        const nuevosErrores = {};
        [...userConfig.obligatorios, ...userConfig.campos.filter(c => !userConfig.obligatorios.includes(c) && formData[c])].forEach(campo => {
            if (validators[campo]) {
                const error = validators[campo](formData[campo]);
                if (error) nuevosErrores[campo] = error;
            }
        });

        setErrores(nuevosErrores);
        if (Object.keys(nuevosErrores).length > 0) return;

        const datosUsuario = mapearCamposDatos(formData, userConfig.campos);
        const idUsuario = usuarioId; 

        try {
            await realizarPeticion(`${API_BASE_URL}/actualizar-usuario/${idUsuario}`, {
                method: 'PUT',
                body: JSON.stringify(datosUsuario),
            });

            const updateSuccess = await updateUserData(); 
            if (updateSuccess) {
                mostrarAlerta('success', 'Usuario actualizado correctamente').then(() => {
                    setIsClosing(true); 
                });
            } else {
                mostrarAlerta('warning', 'Usuario actualizado', 'Los cambios se guardaron pero puede que necesites recargar la página para verlos reflejados').then(() => {
                    setIsClosing(true);
                });
            }
        } catch (error) {
            mostrarAlerta('error', 'Error', error.message || 'Error al actualizar el usuario');
        }
    }, [formData, userConfig, usuarioId, userData, realizarPeticion, mostrarAlerta]);

    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) setIsClosing(true);
    }, []);

    const handleCloseModal = useCallback(() => setIsClosing(true), []);

    const [esEstudiante, esAdmin, esTutor] = [tipoUsuario === 1, tipoUsuario === 2, tipoUsuario === 3];

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div className={`${styles.modalScrollableAniadir} ${abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''}`}>
                    <button onClick={handleCloseModal} type="button" className={styles.closeBtnAniadir}>
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>
                        Editar {esEstudiante ? 'Estudiante' : esAdmin ? 'Administrador' : 'Tutor'}
                    </h2>
                    
                    {loading ? (
                        <div className={styles.loading}>
                            <p>Cargando datos del usuario...</p>
                        </div>
                    ) : (
                        <form className={styles.formUsuario} onSubmit={handleSubmit}>
                            {/* Campos básicos */}
                            {['nombre', 'appa', 'apma'].map(campo => (
                                <div key={campo}>
                                    <label htmlFor={campo}>
                                        {campo === 'nombre' ? 'Nombre' : 
                                         campo === 'appa' ? 'Apellido Paterno' : 'Apellido Materno'}: *
                                        <input
                                            type="text"
                                            id={campo}
                                            value={formData[campo]}
                                            placeholder={`Ingresa ${campo === 'nombre' ? 'el nombre' : 
                                                        campo === 'appa' ? 'el apellido paterno' : 'el apellido materno'}`}
                                            onChange={(e) => handleInputChange(campo, e.target.value)}
                                            required
                                            maxLength="100"
                                        />
                                    </label>
                                    {errores[campo] && <p className={styles.mensajeErrorAniadir}>{errores[campo]}</p>}
                                </div>
                            ))}
                            <label htmlFor="correo">
                                        Correo electrónico: *
                                        <input
                                            type="email"
                                            id="correo"
                                            value={formData.correo}
                                            placeholder="Ingresa el correo"
                                            disabled
                                            onChange={(e) => handleInputChange('correo', e.target.value)}
                                            required
                                            maxLength="100"
                                        />
                                    </label>
                                    {errores.correo && <p className={styles.mensajeErrorAniadir}>{errores.correo}</p>}

                            {/* Campos específicos por tipo de usuario */}
                            {esEstudiante && (
                                <>
                                    <label htmlFor="curp">
                                        CURP:
                                        <input
                                            type="text"
                                            id="curp"
                                            value={formData.curp}
                                            placeholder="Ingresa el CURP"
                                            onChange={(e) => handleInputChange('curp', e.target.value.toUpperCase())}
                                            maxLength="18"
                                        />
                                    </label>
                                    {errores.curp && <p className={styles.mensajeErrorAniadir}>{errores.curp}</p>}

                                    <label htmlFor="fechaNacimiento">
                                        Fecha de Nacimiento:
                                        <input
                                            type="date"
                                            id="fechaNacimiento"
                                            value={formData.fechaNacimiento}
                                            onChange={(e) => handleInputChange('fechaNacimiento', e.target.value)}
                                        />
                                    </label>

                                        {formData.estado === 'Querétaro' && (
                                            <>
                                                    <label className={styles.registroLabel}>Municipio donde estudiaste:</label> 
                                                    <select 
                                                        className={styles.inputForm} 
                                                        value={formData.municipio} 
                                                        onChange={(e) => {
                                                            handleInputChange('municipio', e.target.value);
                                                            handleInputChange('bachillerato', '');
                                                        }}
                                                    >
                                                        <option value="">- Selecciona un municipio -</option>
                                                        {municipios.map((option, index) => (
                                                            <option key={index} value={option.value}>{option.label}</option>
                                                        ))}
                                                    </select> 

                                                    <label className={styles.registroLabel}>Bachillerato donde estudiaste:</label>
                                                    <select 
                                                        className={styles.inputForm} 
                                                        value={formData.bachillerato} 
                                                        onChange={(e) => handleInputChange('bachillerato', e.target.value)}
                                                    >
                                                        <option value="">- Selecciona un bachillerato -</option>
                                                        {bachilleratosMunicipio.map((option, index) => (
                                                            <option key={index} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <label className={styles.registroLabel}>
                                                        Matrícula: 
                                                        <input 
                                                            type="text" 
                                                            className={styles.inputForm} 
                                                            value={formData.matricula} 
                                                            placeholder="Ingresa tu matrícula" 
                                                            onChange={(e) => handleInputChange('matricula', e.target.value)}
                                                        />
                                                    </label>
                                            </>
                                        )}

                                        {/* Si el estado NO es Querétaro, mostrar campos para otros estados */}
                                        {formData.estado && formData.estado !== 'Querétaro' && (
                                            <>
                                                    <label className={styles.registroLabel}>Estado en el que estudiaste:</label>
                                                    <select 
                                                        className={styles.inputForm} 
                                                        value={formData.estado} 
                                                        onChange={(e) => handleInputChange('estado', e.target.value)}
                                                    >
                                                        <option value="">- Selecciona un estado -</option>
                                                        {estados.map((option, index) => (
                                                            <option key={index} value={option.value}>{option.label}</option>
                                                        ))}
                                                    </select>

                                                    <label className={styles.registroLabel}>
                                                            Institución: 
                                                        <input 
                                                            type="text" 
                                                            className={styles.inputForm} 
                                                            value={formData.institucion} 
                                                            placeholder="Ingresa tu institución" 
                                                            onChange={(e) => handleInputChange('institucion', e.target.value)}
                                                        /> 
                                                    </label>
                                                </>
                                        )}

                                    <label className={styles.registroLabel}>
                                        Teléfono de contacto:
                                        <PhoneInput
                                            country={'mx'} 
                                            value={formData.telefono}
                                            onChange={(value) => {
                                                const numeroLimpio = value.replace(/\D/g, '');
                                                handleInputChange('telefono', numeroLimpio);
                                            }}
                                            inputClass={styles.inputTelefono}
                                            containerClass={styles.phoneInputContainer}
                                            placeholder="Ingresa un teléfono personal o del tutor"
                                            preferredCountries={['mx', 'us', 'ca']}
                                            enableSearch={true} 
                                            searchPlaceholder="Buscar país..."
                                        />
                                    </label>
                                    {errores.telefono && <p className={styles.mensajeErrorAniadir}>{errores.telefono}</p>}

                                    {tutorCorreo && (
                                        <label>
                                            Correo del Tutor:
                                            <input
                                                type="email"
                                                value={tutorCorreo}
                                                readOnly
                                                className={styles.inputReadOnly}
                                            />
                                        </label>
                                    )}
                                </>
                            )}

                            {esTutor && (
                                <>
                                    <label className={styles.registroLabel}>
                                        Teléfono de contacto:
                                        <PhoneInput
                                            country={'mx'} 
                                            value={formData.telefono}
                                            onChange={(value) => {
                                                const numeroLimpio = value.replace(/\D/g, '');
                                                handleInputChange('telefono', numeroLimpio);
                                            }}
                                            inputClass={styles.inputTelefono}
                                            containerClass={styles.phoneInputContainer}
                                            placeholder="Ingresa un teléfono personal o del tutor"
                                            preferredCountries={['mx', 'us', 'ca']}
                                            enableSearch={true} 
                                            searchPlaceholder="Buscar país..."
                                        />
                                    </label>
                                    {errores.telefono && <p className={styles.mensajeErrorAniadir}>{errores.telefono}</p>}
                                </>
                            )}

                            <button type="submit" className={styles.botonGuard}>
                                Actualizar
                            </button>
                        </form>
                    )}
            </div>
        </div>
    );
};

export default EditarPerfil;